import { range, map, sortBy, size } from 'lodash';
import React, { useState } from 'react';

import firebase from 'firebase/app';

import { useCollection, useCollectionData, useDocumentData } from 'react-firebase-hooks/firestore';

// ---------- In project imports ----------

import { firestore, functions, FieldValue } from './initFirebase';

import { Button, HGap, LineInput, VGap } from './UI';
import PlayingStateView from './PlayingStateView';
import RoomUsers from './RoomUsers';
import Styles from './Room.module.scss';

import * as Game from './copied/Game';

const leaveRoom = functions.httpsCallable('leaveRoom');
const startGame = functions.httpsCallable('startGame');

const performActionCallable = functions.httpsCallable('performAction');
const performAction =
	(data: { roomId: string, action: Game.Action }) =>
	performActionCallable(data);

const rowsCount = 7;
const colsCount = 10;

function Room(
	{ id, user, isAdmin }
	: { id: string, user: firebase.User, isAdmin: boolean }
) {
	const roomStateRef = firestore.collection('roomStates').doc(id);
	const messagesRef = roomStateRef.collection('messages');

	const [roomState, roomStateLoading, roomStateError] = useDocumentData(roomStateRef);
	const [messagesCollection, messagesLoading, messagesError] = useCollection(messagesRef.orderBy('createdAt', 'asc').limitToLast(40));

	const [newMessageBody, setNewMessageBody] = useState('');
	
	async function leaveRoomTriggered() {
		if (!window.confirm("Are you sure you want to leave the room?"))
			return;

		try {
			await leaveRoom({ roomId: id });
		} catch (error) {
			console.error(error);
		}
	}

	async function startGameTriggered() {
		if (!window.confirm("Are you sure you want to start game? It can't be undone"))
			return;

		try {
			await startGame({ roomId: id });
		} catch (error) {
			console.error(error);
		}
	}

	const actionLauncher = (action: Game.Action, confirmationMessage?: string) => async () => {
		if (confirmationMessage && !window.confirm(confirmationMessage))
			return;

		try {
			await performAction({
				roomId: id,
				action
			});
		} catch (error) {
			console.error(error);
		}
	};

	async function sendMessage(e: React.FormEvent) {
		e.preventDefault();
		setNewMessageBody('');

		try {
			const newMessage = await messagesRef.add({
				authorId: user?.uid,
				createdAt: FieldValue.serverTimestamp(),
				body: newMessageBody,
			});
		} catch (error) {
			console.error(error);
		}
	}

	const joined = roomState?.users?.[user.uid] != null;
	const isOwner = roomState?.ownerId === user.uid;

	const started = roomState?.type && roomState?.type !== 'preparation';
	const preparation = roomState?.type && roomState?.type === 'preparation';
	const playing = roomState?.type && roomState?.type === 'started';
	const finished = roomState?.type && roomState?.type === 'finished';

	const userIdToName = (id: string) =>
		roomState?.users?.[id]?.nickname ?? id.substr(0, 7) + '... (left)';

	return <div className={Styles.room}>
		<div className={Styles.roomContent}>
			{roomStateLoading && <p>Room state loading</p>}
			{roomStateError && <p>Room state error: {JSON.stringify(roomStateError)}</p>}
			<p>
				Room ID: {id} (use it as invite code to join rooms)
				{joined
				&& <> - joined <HGap />
					{roomState?.type === 'preparation' 
					&&  <Button label="Leave room" onClick={leaveRoomTriggered} />}
				</>}
			</p>

			{(isOwner || isAdmin) && preparation &&
			<div>
				Room creator functions: <HGap />
				{!started &&
				 size(roomState?.users ?? {}) >= 3
				 ? <Button label="Start" onClick={startGameTriggered} />
				 : 'Need at least three players to start game'}
			</div>}

			{ preparation
			? <>
				<h2>Game has not started</h2>
				<p>Waiting for room creator to start the game</p>
			</>
			: playing ?
			<h2>Game in progress ...</h2>
			: finished ? <>
				<h2>Game is finished</h2>
				<p>{size(roomState?.users)} players tried to conquer the field of cooperation and trust, but there was only a place of one. All have fallen, but one:</p>
				<h2>
					Winner - {Object.values(roomState?.users as object || {}).find((u: any) => u.health > 0).nickname}
				</h2>
			</>
			: undefined
			}
			<div style={{ height: '115px' }}></div>

			{roomState && roomState.type != 'preparation' &&
				<PlayingStateView
					uid={user.uid}
					isAdmin={isAdmin}
					state={roomState as unknown as Game.PlayingState}
					actionLauncher={actionLauncher} />
			}

			<h3>Chat</h3>
			{messagesCollection?.docs.map(messageDoc => { 
				let message = messageDoc.data();
				return <div key={messageDoc.id}>
					<b>{userIdToName(message.authorId)}</b>: {message.body} (sent { (message.createdAt as firebase.firestore.Timestamp)?.toDate()?.toLocaleString() })
				</div>
			})}
			<form onSubmit={sendMessage}>
				<VGap />
				<LineInput value={newMessageBody} onChange={setNewMessageBody} />
				<VGap />
				<Button label="Send" />
				<VGap />
			</form>

			{isAdmin && 
			<div className={Styles.roomStateJSON}>{JSON.stringify(roomState, undefined, 4)}</div>}
		</div>

		<RoomUsers
			uid={user.uid}
			ownerId={roomState?.ownerId}
			users={roomState?.users ?? {}}
			actionLauncher={actionLauncher}
			roomStateType={roomState?.type}
		/>
	</div>
};

export default Room;
